import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Helmet from 'react-helmet';
import classnames from 'classnames/bind';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import Cookies from 'yoda-core-components/lib/helpers/Cookies/Cookies';
import Loader from 'yoda-core-components/lib/components/Loader/Loader';
import Layout from 'yoda-site-components/lib/components/Layout/Layout';
import ServiceErrorMessage from 'yoda-checkout-components/lib/components/ServiceErrorMessage/ServiceErrorMessage';
import GoogleAdContainer from 'yoda-checkout-components/lib/components/GoogleAdContainer/GoogleAdContainer';
import Canonincalize from 'yoda-account-components/lib/components/Canonincalize/Canonincalize';
import * as ServiceErrorMessageAction from 'yoda-checkout-components/lib/actions/ServiceErrorHandlerAction';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import {
    ANALYTICS_PAGE_NAME_CART_GIFTCARD_LANDING,
    GOOGLE_ADS_GIFTCARD_PAGE_BOTTOM,
    PAGE_TITLES_GIFT_CARD,
} from 'yoda-checkout-components/lib/common/Constants';
import * as CheckoutAnalyticsAction from 'yoda-checkout-components/lib/actions/CheckoutAnalyticsAction';
import siteAnalyticsAction from 'yoda-site-components/lib/actions/AnalyticsAction';
import * as showGlobalLoaderAction from 'yoda-checkout-components/lib/actions/ShowGlobalLoaderAction';
import withBoundaryError from 'yoda-checkout-components/lib/components/ErrorBoundary/ErrorBoundaryHOC';
import orderActions from 'yoda-site-components/lib/actions/OrderAction';
import splunkLogsActions from 'yoda-checkout-components/lib/actions/SplunkLogsActions';
import { setGlobalErrorHandler } from 'yoda-checkout-components/lib/helpers/Utils/Utils';
import GiftCardForm from './components/GiftCardForm';
import CardGrid from './components/UIComponents/CardGrid/CardGrid';
import {
    getGiftCardBalance,
    updateGiftCard,
    setGiftCardError,
} from '../../../actions/GiftCardActions';
import { runSingleValidator } from './utility/giftCardValidation';
import Constants from './utility/Constants';
import * as styles from './GiftCardBalance.css';

const cx = classnames.bind(styles);
const section = 'GIFT_CARD_BALANCE';

export class GiftCardBalance extends Component {
    static defaultProps = {
        giftCard: {},
        actions: {},
        context: {},
        selectedStore: {},
        serviceErrors: {},
        location: {},
    };

    static propTypes = {
        giftCard: PropTypes.objectOf(PropTypes.any),
        actions: PropTypes.objectOf(PropTypes.func),
        context: PropTypes.objectOf(PropTypes.any),
        showGlobalLoader: PropTypes.bool.isRequired,
        selectedStore: PropTypes.objectOf(PropTypes.any),
        serviceErrors: PropTypes.objectOf(PropTypes.any),
        location: PropTypes.objectOf(PropTypes.any),
    };

    constructor() {
        super();
        this.onChange = this.handleChange.bind(this);
        this.onSubmit = this.handleSubmit.bind(this);
        this.onCaptchaChange = this.handleCaptchaChange.bind(this);
        this.errorSubscription = () => {};
        this.state = {
            contextList: ['GIFTCARDBALANCE'],
        };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount() {
        const captchaCookie = Cookies.load('gc_captcha');
        const invisibleFlag = this.props.context.featureFlags.enableInvisibleGiftCardRecaptcha;
        let captchaType = '';
        /* istanbul ignore next */
        if (invisibleFlag && captchaCookie === '1') {
            captchaType = Constants.INVISIBLE;
        } else {
            captchaType = Constants.VISIBLE;
        }
        this.props.actions.updateGiftCard({
            captchaType,
        });
    }

    componentDidMount() {
        // DA => trigger page load event
        this.props.actions.pageLoadSuccessAnalyticsAction(
            ANALYTICS_PAGE_NAME_CART_GIFTCARD_LANDING
        );
        // Populate store if user lands in this page directly from url
        /* istanbul ignore else */
        if (isEmpty(get(this.props.selectedStore, 'storeDetails'))) {
            this.props.actions.getStoreByStoreId({});
        }

        const enableGlobalErrorLogs = get(
            this.props.context,
            'featureFlags.splunkLogs.enableGlobalErrorLogs'
        );
        /* istanbul ignore next */
        if (enableGlobalErrorLogs) {
            this.errorSubscription = setGlobalErrorHandler(
                section,
                this.props.actions.logGlobalError
            );
        }
    }

    componentWillUnmount() {
        this.errorSubscription();
    }

    handleChange(event, eventProps) {
        if (event.type === 'blur') {
            const error = runSingleValidator(eventProps.field, eventProps.data);
            this.props.actions.setGiftCardError(error);
        } else if (
            eventProps &&
            eventProps.field === 'giftCardNum' &&
            eventProps.data.giftCardNum &&
            eventProps.data.giftCardNum.length === 19
        ) {
            this.props.actions.setGiftCardError({ giftCardNumError: '' });
        } else if (
            eventProps &&
            eventProps.field === 'giftCardPin' &&
            eventProps.data.giftCardPin &&
            eventProps.data.giftCardPin.length === 4
        ) {
            this.props.actions.setGiftCardError({ giftCardPinError: '' });
        }
        this.props.actions.updateGiftCard(eventProps.data);
        this.props.actions.clearServiceErrorMessage();
    }

    handleSubmit() {
        // Trigger global loader
        this.props.actions.showGlobalLoader(true);
        // call api as client side validation passed
        this.props.actions.getGiftCardBalance({
            cardNumber: this.props.giftCard.giftCardNum,
            pin: this.props.giftCard.giftCardPin,
            captchaToken: this.props.giftCard.captchaToken,
            captchaType: this.props.giftCard.captchaType,
        });
        // reset the errors
        this.props.actions.setGiftCardError({ giftCardNumError: '', giftCardPinError: '' });
    }

    handleCaptchaChange(response) {
        this.props.actions.updateGiftCard({
            captchaToken: response,
        });
        /* istanbul ignore else */
        if (this.props.giftCard.captchaType === Constants.INVISIBLE) {
            this.onSubmit();
        }
    }

    render() {
        const { serviceErrors, location } = this.props;
        const serviceErrCode = get(serviceErrors, 'errors.errorCode', '');
        const excludederrCode = ['SRV_ORDER_NOT_EXISTS', 'SRV_STORE_NOTFOUND'];
        const { enableFreestarAdComponent = true, enableGiftcardBv = true } =
            this.props.context?.featureFlags || {};
        const isDesktop = get(this.props.context, 'deviceType.isDesktop', false);
        return (
            <Layout>
                <Helmet title={PAGE_TITLES_GIFT_CARD} />
                <Canonincalize urlLocation={location.pathname} />
                <div
                    className={`${cx('giftCardContainer', 'tailwindGrid')} ${dt([
                        'mt-0',
                        'mx-6',
                        'mb-6',
                        'text-center',
                    ])}`}
                >
                    <div
                        className={`${cx('topWrapper')} ${dt([
                            'flex',
                            'flex-col',
                            'content-center',
                            'items-center',
                            'smOnly:mt-6',
                        ])}`}
                    >
                        {!excludederrCode.includes(serviceErrCode) && (
                            <ServiceErrorMessage
                                componentName="GIFT_BALANCE"
                                contextList={this.state.contextList}
                            />
                        )}
                    </div>
                    <section
                        className={`${cx('giftCardBlock', 'sm12', 'md6', 'lg4', 'xl4')} ${dt([
                            'h-full',
                            'mx-auto',
                            'block',
                            'text-left',
                            'mt-5',
                        ])}`}
                    >
                        {this.props.showGlobalLoader && <Loader keepOverlay />}
                        <GiftCardForm
                            giftCard={this.props.giftCard}
                            context={this.props.context}
                            onCaptchaChange={this.onCaptchaChange}
                            onChange={this.onChange}
                            onSubmit={this.onSubmit}
                            setGiftCardError={this.props.actions.setGiftCardError}
                            triggerFormError={this.props.actions.triggerFormError}
                        />
                        {!isEmpty(this.props.giftCard.giftCardBalances) && (
                            <CardGrid
                                giftCards={this.props.giftCard.giftCardBalances}
                                context={this.props.context}
                            />
                        )}
                    </section>
                    {enableGiftcardBv && (
                        <section className={dt(['pt-4'])}>
                            {isDesktop && (
                                <GoogleAdContainer
                                    displayId={GOOGLE_ADS_GIFTCARD_PAGE_BOTTOM}
                                    enableFreestarAdComponent={enableFreestarAdComponent}
                                />
                            )}
                        </section>
                    )}
                </div>
            </Layout>
        );
    }
}

const mapStateToProps = ({
    giftCard,
    context,
    showGlobalLoader,
    selectedStore,
    serviceErrors,
}) => ({
    giftCard,
    context,
    showGlobalLoader,
    selectedStore,
    serviceErrors,
});

const mapDispatchToProps = (dispatch) => ({
    actions: bindActionCreators(
        {
            getGiftCardBalance,
            updateGiftCard,
            setGiftCardError,
            ...ServiceErrorMessageAction,
            ...orderActions,
            ...splunkLogsActions,
            ...CheckoutAnalyticsAction,
            ...siteAnalyticsAction,
            ...showGlobalLoaderAction,
        },
        dispatch
    ),
});
export const GiftCardBalanceConnected = connect(
    mapStateToProps,
    mapDispatchToProps
)(GiftCardBalance);
export default withBoundaryError(GiftCardBalanceConnected, section);
